import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import {
  archiveAccountKbSection,
  listAccountKbSections,
  reviewAccountKbSection,
  saveAccountKbSection,
} from 'services/agentKb';

// The account's own knowledge layer.
//
// Unlike the system KB (AgentKbProvider), this reads through the endpoint
// rather than Firestore listeners: sections carry server-derived fields
// (triggerKeys, the proposal pointers) and every write already goes through the
// endpoint, so one round trip after each mutation keeps the screen honest
// without a second source of truth.

interface AccountKnowledgeContextType {
  sections: AccountKb.Section[];
  validation: AccountKb.ValidationResult | null;
  rights: AgentKb.Rights;
  isLoading: boolean;
  refresh: () => Promise<void>;
  saveSection: (params: {
    sectionId: string;
    title: string;
    summary: string;
    tier: AccountKb.Tier;
    triggers: AccountKb.Trigger[];
    body: string;
    baseRev?: number;
  }) => Promise<boolean>;
  archiveSection: (sectionId: string) => Promise<boolean>;
  reviewSection: (params: {
    sectionId: string;
    decision: 'accept' | 'reject';
    expectedProposedRev: number;
    body?: string;
    tier?: AccountKb.Tier;
    triggers?: AccountKb.Trigger[];
  }) => Promise<boolean>;
}

const NO_RIGHTS: AgentKb.Rights = {
  canRead: false,
  canEdit: false,
  canPublish: false,
};

export const AccountKnowledgeContext =
  createContext<AccountKnowledgeContextType>({
    sections: [],
    validation: null,
    rights: NO_RIGHTS,
    isLoading: true,
    refresh: () => {
      throw new Error('refresh used outside AccountKnowledgeProvider');
    },
    saveSection: () => {
      throw new Error('saveSection used outside AccountKnowledgeProvider');
    },
    archiveSection: () => {
      throw new Error('archiveSection used outside AccountKnowledgeProvider');
    },
    reviewSection: () => {
      throw new Error('reviewSection used outside AccountKnowledgeProvider');
    },
  });

const AccountKnowledgeProvider = ({ children }: { children: ReactNode }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);

  const [sections, setSections] = useState<AccountKb.Section[]>([]);
  const [validation, setValidation] =
    useState<AccountKb.ValidationResult | null>(null);
  const [rights, setRights] = useState<AgentKb.Rights>(NO_RIGHTS);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const refresh = useCallback(async (): Promise<void> => {
    const result = await listAccountKbSections(api);
    if (result.ok) {
      setSections(result.data.sections);
      setValidation(result.data.validation);
      setRights(result.data.rights);
    } else {
      showToast(result.error);
    }
    setIsLoading(false);
  }, [api, showToast]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setSections([]);
    refresh().then(() => {
      if (!isActive) {
        return;
      }
    });
    return () => {
      isActive = false;
    };
  }, [refresh, selectedAccountId]);

  const saveSection: AccountKnowledgeContextType['saveSection'] = useCallback(
    async (params) => {
      const result = await saveAccountKbSection(api, params);
      if (!result.ok) {
        // Never discard the author's text on a refusal — the message says what
        // to fix (a stale revision, or a rule the section breaks).
        showToast(result.error);
        return false;
      }
      showToast(`Saved "${params.title}"`);
      await refresh();
      return true;
    },
    [api, refresh, showToast],
  );

  const archiveSection = useCallback(
    async (sectionId: string): Promise<boolean> => {
      const result = await archiveAccountKbSection(api, { sectionId });
      if (!result.ok) {
        showToast(result.error);
        return false;
      }
      showToast('Section retired');
      await refresh();
      return true;
    },
    [api, refresh, showToast],
  );

  const reviewSection: AccountKnowledgeContextType['reviewSection'] =
    useCallback(
      async (params) => {
        const result = await reviewAccountKbSection(api, params);
        if (!result.ok) {
          showToast(result.error);
          return false;
        }
        showToast(
          params.decision === 'accept'
            ? 'Accepted — it is live for this account'
            : 'Rejected',
        );
        await refresh();
        return true;
      },
      [api, refresh, showToast],
    );

  const value = useMemo(
    () => ({
      sections,
      validation,
      rights,
      isLoading,
      refresh,
      saveSection,
      archiveSection,
      reviewSection,
    }),
    [
      sections,
      validation,
      rights,
      isLoading,
      refresh,
      saveSection,
      archiveSection,
      reviewSection,
    ],
  );

  return (
    <AccountKnowledgeContext.Provider value={value}>
      {children}
    </AccountKnowledgeContext.Provider>
  );
};

export default AccountKnowledgeProvider;
